import { AlertCircle, AlertTriangle, CheckCircle2, Info, type LucideIcon, X } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Icon } from '../atoms/Icon'

/**
 * StatusBanner — global top-of-page banner for maintenance, trial, errors.
 * Mount inside AppShell above TopHeader or just under it.
 */
export interface StatusBannerProps extends React.HTMLAttributes<HTMLDivElement> {
  tone?: 'info' | 'success' | 'warning' | 'error' | 'maintenance' | 'trial'
  icon?: LucideIcon
  onDismiss?: () => void
  action?: React.ReactNode
}

const toneClass = {
  info: 'bg-ia-info-bg text-ia-text border-b-ia-info/30',
  success: 'bg-ia-success-bg text-ia-text border-b-ia-success/30',
  warning: 'bg-ia-warning-bg text-ia-text border-b-ia-warning/30',
  error: 'bg-ia-error-bg text-ia-text border-b-ia-error/30',
  maintenance: 'bg-ia-warning-bg text-ia-text border-b-ia-warning/30',
  trial: 'bg-ia-info-bg text-ia-text border-b-ia-info/30',
} as const

const toneIcon: Record<NonNullable<StatusBannerProps['tone']>, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: AlertCircle,
  maintenance: AlertTriangle,
  trial: Info,
}

export const StatusBanner = React.forwardRef<HTMLDivElement, StatusBannerProps>(
  ({ className, tone = 'info', icon, onDismiss, action, children, ...props }, ref) => (
    <div
      ref={ref}
      role="status"
      className={cn(
        'flex items-center gap-3 px-6 h-10 border-b text-body-sm',
        toneClass[tone],
        className,
      )}
      {...props}
    >
      <Icon icon={icon ?? toneIcon[tone]} size="sm" />
      <div className="flex-1 min-w-0 truncate">{children}</div>
      {action && <div className="shrink-0">{action}</div>}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="rounded-pill h-7 w-7 flex items-center justify-center text-ia-text-tertiary transition-all hover:bg-[var(--color-ia-sidebar-active-glow)] hover:text-[var(--color-ia-sidebar-active-fg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X size={14} strokeWidth={1.75} />
        </button>
      )}
    </div>
  ),
)
StatusBanner.displayName = 'StatusBanner'
